import React, { useState, useEffect } from 'react';
import { addonAPI, branchAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './Addons.css';

const TYPES = [
    { value: 'extra_data', label: 'Extra Data', unit: 'MB', field: 'dataMB' },
    { value: 'extra_speed', label: 'Extra Speed', unit: 'Kbps', field: 'speedKbps' },
    { value: 'extra_device', label: 'Extra Device', unit: 'devices', field: 'devices' },
    { value: 'extra_validity', label: 'Extra Validity', unit: 'minutes', field: 'validityMinutes' },
    { value: 'unlimited_pass', label: 'Unlimited Pass', unit: '', field: null },
];

const DEFAULT_FORM = {
    name: '', type: 'extra_data', description: '', branchId: '',
    value: { dataMB: 0, speedKbps: 0, devices: 0, validityMinutes: 0 },
    price: 0, currency: 'INR', isActive: true,
};

const Addons = () => {
    const { selectedBranch } = useBranchStore();
    const [addons, setAddons] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => { fetchAll(); }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [addonRes, branchRes] = await Promise.all([
                addonAPI.getAll(branchId ? { branchId } : undefined),
                branchAPI.getAll(),
            ]);
            setAddons(addonRes.data.data || []);
            setBranches(branchRes.data.data.branches || []);
        } catch (error) {
            toast.error('Failed to fetch add-ons');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => { setEditing(null); setFormData({ ...DEFAULT_FORM, branchId: selectedBranch?._id || '' }); setShowModal(true); };

    const handleEdit = (addon) => {
        setEditing(addon);
        setFormData({
            name: addon.name, type: addon.type, description: addon.description || '',
            branchId: addon.branchId?._id || addon.branchId || '',
            value: { ...addon.value }, price: addon.price, currency: addon.currency, isActive: addon.isActive !== false,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return toast.warning('Name is required');
        try {
            if (editing) {
                await addonAPI.update(editing._id, formData);
                toast.success('Add-on updated');
            } else {
                await addonAPI.create(formData);
                toast.success('Add-on created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (addon) => {
        if (!window.confirm(`Delete add-on "${addon.name}"?`)) return;
        try {
            await addonAPI.delete(addon._id);
            toast.success('Add-on deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const typeMeta = (v) => TYPES.find(t => t.value === v) || TYPES[0];
    const activeField = typeMeta(formData.type).field;

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-shopping-cart"></i> Internet Add-Ons</h1>
                        <p className="page-subtitle-modern">Purchasable extras — extra data, speed, devices, or validity, applied on top of a guest's current plan</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}><i className="fas fa-plus-circle"></i> Add Add-on</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading add-ons...</p></div>
            ) : addons.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-shopping-cart"></i></div>
                    <h3>No Add-Ons Yet</h3>
                    <p>e.g. "+10 Mbps" speed boost, "+1 GB" data top-up, "+2 hours" validity extension</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {addons.map(addon => {
                        const meta = typeMeta(addon.type);
                        return (
                            <div key={addon._id} className="resource-card-modern">
                                <div className="card-header">
                                    <div className="resource-avatar" style={{ background: '#f59e0b15', color: '#f59e0b' }}>
                                        <i className="fas fa-bolt"></i>
                                    </div>
                                    <div className="resource-title">
                                        <h3>{addon.name}</h3>
                                        <span className="resource-subtitle">{meta.label}</span>
                                    </div>
                                    <div className="card-actions">
                                        <button className="action-btn-icon edit" onClick={() => handleEdit(addon)}><i className="fas fa-edit"></i></button>
                                        <button className="action-btn-icon delete" onClick={() => handleDelete(addon)}><i className="fas fa-trash"></i></button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="status-badges">
                                        <span className={`status-badge ${addon.isActive ? 'active' : 'inactive'}`}><span className="badge-dot"></span>{addon.isActive ? 'Active' : 'Inactive'}</span>
                                    </div>
                                    <div className="info-grid">
                                        {meta.field && (
                                            <div className="info-item"><i className="fas fa-plus"></i><div><span className="info-label">Grants</span><span className="info-value">{addon.value[meta.field]} {meta.unit}</span></div></div>
                                        )}
                                        <div className="info-item"><i className="fas fa-rupee-sign"></i><div><span className="info-label">Price</span><span className="info-value">₹{addon.price}</span></div></div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit Add-on' : 'Add Add-on'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. +10 Mbps Speed Boost" required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Type</label>
                                        <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                                            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Description</label>
                                        <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Branch</label>
                                        <select value={formData.branchId} onChange={e => setFormData({ ...formData, branchId: e.target.value })}>
                                            <option value="">All Branches</option>
                                            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    </div>
                                    {activeField && (
                                        <div className="form-group-modern">
                                            <label>{typeMeta(formData.type).label} ({typeMeta(formData.type).unit})</label>
                                            <input
                                                type="number"
                                                value={formData.value[activeField]}
                                                onChange={e => setFormData({ ...formData, value: { ...formData.value, [activeField]: Number(e.target.value) } })}
                                            />
                                        </div>
                                    )}
                                    <div className="form-group-modern">
                                        <label>Price (₹) <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="number" min="0" value={formData.price} onChange={e => setFormData({ ...formData, price: Number(e.target.value) })} required />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Addons;
